import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { CheckCircle, XCircle, Maximize2, X, Save } from 'lucide-react';

import { useAppContext } from '../../contexts/AppContext';
import { FmeaItem, ChangeRecord, EditableFmeaRow } from '../../types';
import { normalizeProjectName } from '../../utils/validation';
import { getStandardConfig } from '../../constants/fmeaStandards';
import { calculateActionPriority, calculateRPN, getRiskLevel } from '../../utils/riskCalculators';
import { ControlSelectionModal } from '../../components/ControlSelectionModal';

import { filterFmeaItems, sortFmeaItems, FilterableColumn } from '../../utils/fmeaTableUtils';
import { generateAiTasks } from '../../utils/fmeaAiTasks';
import { NextStepButton } from '../../components/WorkflowNavigation';

import { EditorToolbar } from './EditorToolbar';
import { FmeaEditorTable } from './FmeaEditorTable';
import { LayoutSettingsPanel, LayoutSettings, DEFAULT_LAYOUT_SETTINGS } from './LayoutSettingsPanel';

type TrackedField = 'function' | 'itemFunction' | 'requirement' | 'failureMode' | 'effects' | 'prevControls' | 'detControls' | 'S' | 'O' | 'D' | 'specialCharacteristic' | 'recommendedAction' | 'actionResponsibility' | 'actionTargetDate' | 'actionTaken' | 'actionCompletionDate';

const FmeaEditorPage: React.FC = () => {
    const { allData, masterFunctions, updateFmeaData, updateFmeaItem, activeProject, projectList, exportProject, isFullScreen, setIsFullScreen } = useAppContext();
    const [editorData, setEditorData] = useState<EditableFmeaRow[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isCommentModalOpen, setCommentModalOpen] = useState(false);
    const [currentChange, setCurrentChange] = useState<{ rowIndex: number, field: TrackedField } | null>(null);

    // Control Selection Modal State
    const [controlModal, setControlModal] = useState<{ isOpen: boolean, rowIndex: number, type: 'prevention' | 'detection', value: string }>({
        isOpen: false, rowIndex: -1, type: 'prevention', value: ''
    });

    const openControlModal = (rowIndex: number, type: 'prevention' | 'detection', value: string) => {
        setControlModal({ isOpen: true, rowIndex, type, value });
    };

    const handleControlSelect = (selectedString: string) => {
        const { rowIndex, type } = controlModal;
        const field = type === 'prevention' ? 'prevControls' : 'detControls';
        handleCellChange(rowIndex, field, selectedString);
        setControlModal(prev => ({ ...prev, isOpen: false }));
    };

    const filteredControls = useMemo(() => {
        return allData.controls || [];
    }, [allData.controls]);

    const [comment, setComment] = useState('');
    const [user, setUser] = useState('');
    const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<Record<TrackedField, string | number>>>>({});
    const pendingChangesRef = useRef(pendingChanges);
    useEffect(() => { pendingChangesRef.current = pendingChanges; }, [pendingChanges]);
    const [isHistoryModalOpen, setHistoryModalOpen] = useState<boolean>(false);
    const [selectedHistory, setSelectedHistory] = useState<ChangeRecord[] | undefined>([]);
    const [selectedFmeaId, setSelectedFmeaId] = useState<string | null>(null);
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
    const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>(DEFAULT_LAYOUT_SETTINGS);
    const [layoutSettingsOpen, setLayoutSettingsOpen] = useState(false);
    const isResizing = useRef<string | null>(null);
    const tableRef = useRef<HTMLTableElement>(null);

    // Cell Popout Modal State (read/edit full text)
    const [cellPopout, setCellPopout] = useState<{ rowIndex: number, field: TrackedField, value: string, label: string } | null>(null);
    const [popoutEditValue, setPopoutEditValue] = useState('');

    const [columnFilters, setColumnFilters] = useState<Record<FilterableColumn, Set<string>>>(
        { id: new Set(), function: new Set(), itemFunction: new Set(), specialCharacteristic: new Set() }
    );
    const [activeFilterDropdown, setActiveFilterDropdown] = useState<FilterableColumn | null>(null);

    useEffect(() => {
        const savedWidths = localStorage.getItem('fmeaColumnWidths');
        if (savedWidths) {
            setColumnWidths(JSON.parse(savedWidths));
        }
    }, []);

    const transformToEditable = (fmeaItems: FmeaItem[]): EditableFmeaRow[] => (fmeaItems || []).map(item => ({
        ...item,
        prevControls: (item.preventionControls || []).join(';'),
        detControls: (item.detectionControls || []).join(';'),
    }));

    const transformToFmeaItem = (editableRow: EditableFmeaRow): FmeaItem => {
        const { prevControls, detControls, ...fmeaItemBase } = editableRow;
        return {
            ...fmeaItemBase,
            preventionControls: prevControls.split(';').map(s => s.trim()).filter(Boolean),
            detectionControls: detControls.split(';').map(s => s.trim()).filter(Boolean),
            RPN: calculateRPN(editableRow.S, editableRow.O, editableRow.D),
            actionPriority: calculateActionPriority(editableRow.S, editableRow.O, editableRow.D)
        };
    };

    useEffect(() => {
        const activeProjectNormalized = normalizeProjectName(activeProject);
        const filteredItems = (allData.fmeaItems || []).filter(item => {
            if (activeProject === 'all') return true;
            if (item.projectName) {
                return normalizeProjectName(item.projectName) === activeProjectNormalized;
            }
            if (!item.projectName && !item.epochId) {
                return activeProjectNormalized === 'aerator pro';
            }
            return false;
        });
        
        setEditorData(prevData => {
            const transformedServerData = transformToEditable(filteredItems);
            if (prevData.length === 0) return transformedServerData;
            
            const serverDataMap = new Map(transformedServerData.map(d => [d.id, d]));
            const nextData: EditableFmeaRow[] = [];
            const processedIds = new Set<string>();

            for (const localRow of prevData) {
                processedIds.add(localRow.id);
                const hasPending = pendingChangesRef.current[localRow.id] && Object.keys(pendingChangesRef.current[localRow.id]).length > 0;
                const serverRow = serverDataMap.get(localRow.id);
                
                if (hasPending || localRow.id.startsWith('FMEA-NEW-')) {
                    nextData.push(localRow);
                } else if (serverRow) {
                    nextData.push(serverRow);
                }
            }
            
            for (const serverRow of transformedServerData) {
                if (!processedIds.has(serverRow.id)) {
                    nextData.push(serverRow);
                }
            }
            
            return nextData;
        });
    }, [allData.fmeaItems, activeProject]);

    const uniqueOptions = useMemo(() => {
        const options: Record<string, Set<string>> = {
            id: new Set(), function: new Set(masterFunctions), itemFunction: new Set(), specialCharacteristic: new Set(['CC', 'SC', 'none'])
        };
        editorData.forEach(item => {
            (Object.keys(options) as string[]).forEach(key => {
                const val = (item as any)[key];
                if (val !== undefined && val !== null) options[key].add(String(val));
            });
        });
        const sorted: Record<string, string[]> = {};
        for (const k in options) sorted[k] = Array.from(options[k]).sort();
        return sorted;
    }, [editorData, masterFunctions]);

    const handleCellChange = useCallback((index: number, field: keyof EditableFmeaRow, value: string | number) => {
        setEditorData(prevData => {
            const newData = [...prevData];
            if (newData[index][field] === value) return prevData;
            const item = { ...newData[index], [field]: value };
            newData[index] = item;

            const auditableFields: TrackedField[] = [
                'function', 'itemFunction', 'requirement', 'failureMode', 'effects', 'prevControls', 'detControls', 'S', 'O', 'D',
                'recommendedAction', 'actionResponsibility'
            ];
            if (auditableFields.includes(field as TrackedField)) {
                setPendingChanges(prev => ({ ...prev, [item.id]: { ...prev[item.id], [field]: value } }));
            }
            return newData;
        });
    }, []);

    const openCommentModal = (rowIndex: number, field: TrackedField) => {
        setCurrentChange({ rowIndex, field });
        setCommentModalOpen(true);
    };

    const handleCancelChange = (rowIndex: number, field: TrackedField) => {
        const itemToUpdate = editorData[rowIndex];
        const originalItem = allData.fmeaItems.find(i => i.id === itemToUpdate.id);
        let originalValue: string | number = '';
        if (originalItem) {
            if (field === 'prevControls') originalValue = originalItem.preventionControls.join(';');
            else if (field === 'detControls') originalValue = originalItem.detectionControls.join(';');
            else originalValue = (originalItem as any)[field] || '';
        }
        setEditorData(prev => {
            const newData = [...prev];
            newData[rowIndex] = { ...newData[rowIndex], [field]: originalValue };
            return newData;
        });
        setPendingChanges(prev => {
            const newP = { ...prev };
            if (newP[itemToUpdate.id]) {
                delete newP[itemToUpdate.id][field];
                if (Object.keys(newP[itemToUpdate.id]).length === 0) delete newP[itemToUpdate.id];
            }
            return newP;
        });
    };

    const handleCommitChange = async () => {
        if (!currentChange || !user || !comment) return alert("Sign-off required");
        const { rowIndex, field } = currentChange;
        const item = editorData[rowIndex];
        const oldValue = 'History Recorded'; // Simplified for refactor
        const newValue = pendingChanges[item.id]?.[field];
        if (newValue === undefined) return;

        const record: ChangeRecord = { timestamp: new Date().toISOString(), user, field, oldValue: String(oldValue), newValue: String(newValue), comment };
        const updatedRow = { ...item, changeHistory: [...(item.changeHistory || []), record] };
        await updateFmeaItem(transformToFmeaItem(updatedRow));

        setEditorData(prev => {
            const d = [...prev];
            d[rowIndex] = updatedRow;
            return d;
        });
        setPendingChanges(prev => {
            const p = { ...prev };
            delete p[item.id][field];
            if (Object.keys(p[item.id]).length === 0) delete p[item.id];
            return p;
        });
        setCommentModalOpen(false);
        setComment('');
    };

    const openHistoryModal = (item: EditableFmeaRow) => {
        setSelectedFmeaId(item.id);
        setSelectedHistory(item.changeHistory);
        setHistoryModalOpen(true);
    };

    const openCellPopout = (rowIndex: number, field: TrackedField) => {
        const val = String(editorData[rowIndex][field] || '');
        setCellPopout({ rowIndex, field, value: val, label: field.toUpperCase() });
        setPopoutEditValue(val);
    };

    const handlePopoutSave = () => {
        if (cellPopout && popoutEditValue !== cellPopout.value) {
            handleCellChange(cellPopout.rowIndex, cellPopout.field, popoutEditValue);
            openCommentModal(cellPopout.rowIndex, cellPopout.field);
        }
        setCellPopout(null);
    };

    const addRow = () => {
        const newRow: EditableFmeaRow = {
            id: `FMEA-NEW-${Date.now()}`, function: masterFunctions[0], itemFunction: '', requirement: '', failureMode: '', effects: '',
            S: 1, O: 1, D: 1, prevControls: '', detControls: '', changeHistory: [], status: 'draft', notes: '', specialCharacteristic: 'none',
            recommendedAction: '', actionResponsibility: '', actionTargetDate: '', actionTaken: '', actionCompletionDate: '',
            projectName: activeProject !== 'all' ? activeProject : undefined
        };
        setEditorData(prev => [...prev, newRow]);
    };

    const duplicateLastRow = () => {
        if (editorData.length === 0) return;
        const last = editorData[editorData.length - 1];
        setEditorData(prev => [...prev, { ...last, id: `FMEA-NEW-${Date.now()}`, changeHistory: [] }]);
    };

    const toggleRowSelection = (id: string) => {
        const s = new Set(selectedRowIds);
        if (s.has(id)) s.delete(id); else s.add(id);
        setSelectedRowIds(s);
    };

    const toggleSelectAll = () => {
        setSelectedRowIds(selectedRowIds.size === filteredData.length ? new Set() : new Set(filteredData.map(d => d.id)));
    };

    const deleteSelectedRows = () => {
        if (selectedRowIds.size === 0) return;
        if (window.confirm("Delete selected?")) {
            setEditorData(prev => prev.filter(r => !selectedRowIds.has(r.id)));
            setSelectedRowIds(new Set());
        }
    };

    const handleSave = async () => {
        if (window.confirm("Save to DB?")) {
            await updateFmeaData(editorData.map(transformToFmeaItem));
            alert("Saved");
        }
    };

    const resetFilters = () => {
        setSearchTerm('');
        setColumnFilters({ id: new Set(), function: new Set(), itemFunction: new Set(), specialCharacteristic: new Set() });
    };

    const [selectedEpochFilter, setSelectedEpochFilter] = useState('all');
    const [viewMode, setViewMode] = useState<'active' | 'draft'>('active');

    const filteredData = useMemo(() => {
        return filterFmeaItems(editorData, { searchTerm, columnFilters, selectedEpochFilter, activeProject, viewMode }, allData.iterationHistory);
    }, [editorData, searchTerm, columnFilters, selectedEpochFilter, activeProject, viewMode]);

    const [sortConfig, setSortConfig] = useState<{ key: string | null, direction: 'asc' | 'desc' | null }>({ key: null, direction: null });
    const handleSort = (key: string) => {
        let dir: 'asc' | 'desc' | null = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') dir = 'desc';
        else if (sortConfig.key === key && sortConfig.direction === 'desc') dir = null;
        setSortConfig({ key: dir ? key : null, direction: dir });
    };

    const sortedData = useMemo(() => sortFmeaItems(filteredData, sortConfig), [filteredData, sortConfig]);

    const handleMouseDown = (key: string) => { isResizing.current = key; };
    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing.current || !tableRef.current) return;
        const th = tableRef.current.querySelector(`th[data-col-key="${isResizing.current}"]`) as HTMLElement;
        if (!th) return;
        const w = e.clientX - th.getBoundingClientRect().left;
        if (w > 30) setColumnWidths(prev => ({ ...prev, [isResizing.current!]: w }));
    }, []);

    const handleMouseUp = useCallback(() => {
        if (isResizing.current) localStorage.setItem('fmeaColumnWidths', JSON.stringify(columnWidths));
        isResizing.current = null;
    }, [columnWidths]);

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove); window.addEventListener('mouseup', handleMouseUp);
        return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }, [handleMouseMove, handleMouseUp]);

    const standardConfig = useMemo(() => getStandardConfig(allData.projectMetadata?.fmeaStandard), [allData.projectMetadata?.fmeaStandard]);

    const getMetricColor = (val: any, isAP: boolean = false) => {
        if (isAP) {
            if (val === 'H') return 'bg-red-500 text-white';
            if (val === 'M') return 'bg-yellow-100 text-yellow-700';
            return 'bg-emerald-50 text-emerald-700';
        }
        const n = Number(val);
        if (n >= 120) return 'bg-red-500 text-white';
        if (n >= 70) return 'bg-amber-100 text-amber-700';
        return 'text-text-secondary';
    };

    return (
        <div className="flex flex-col h-full w-full bg-background text-text-primary overflow-hidden" style={{ minHeight: 0 }}>
            {/* Fixed toolbar at top */}
            <div className="shrink-0 px-2 pt-2">
                <EditorToolbar
                    activeProject={activeProject}
                    productName={allData.products.find(p => p.projectName === activeProject)?.name}
                    productDescription={allData.products.find(p => p.projectName === activeProject)?.description}
                    standardConfig={standardConfig}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    activeCount={editorData.filter(i => (i.status || 'draft') === 'active').length}
                    draftCount={editorData.filter(i => (i.status || 'draft') === 'draft').length}
                    addRow={addRow}
                    duplicateLastRow={duplicateLastRow}
                    deleteSelectedRows={deleteSelectedRows}
                    selectedCount={selectedRowIds.size}
                    handleAiGenerate={() => generateAiTasks(editorData, setEditorData)}
                    resetFilters={resetFilters}
                    wrapText={layoutSettings.wrapText}
                    setWrapText={(val) => setLayoutSettings(prev => ({ ...prev, wrapText: val }))}
                    handleSave={handleSave}
                    exportProject={(type: 'csv' | 'json') => exportProject(type)}
                    onOpenLayoutSettings={() => setLayoutSettingsOpen(true)}
                    rowCount={editorData.length}
                    isFullScreen={isFullScreen}
                    setIsFullScreen={setIsFullScreen}
                />
            </div>

            {/* Scrollable table area — fills remaining space */}
            <div className="flex-1 overflow-y-auto overflow-x-auto px-2 pb-2" style={{ minHeight: 0 }}>
                <FmeaEditorTable
                    ref={tableRef}
                    data={sortedData}
                    standardConfig={standardConfig}
                    selectedRowIds={selectedRowIds}
                    toggleRowSelection={toggleRowSelection}
                    toggleSelectAll={toggleSelectAll}
                    handleSort={handleSort}
                    sortConfig={sortConfig}
                    columnWidths={columnWidths}
                    handleMouseDown={handleMouseDown}
                    columnFilters={columnFilters}
                    toggleFilterDropdown={(c: FilterableColumn) => setActiveFilterDropdown(prev => prev === c ? null : c)}
                    activeFilterDropdown={activeFilterDropdown}
                    handleSelectAllColumnFilter={(c: FilterableColumn) => {
                        const opts = uniqueOptions[c];
                        setColumnFilters(prev => ({ ...prev, [c]: prev[c].size === opts.length ? new Set() : new Set(opts) }));
                    }}
                    handleColumnFilterChange={(c: FilterableColumn, v: string) => {
                        setColumnFilters(prev => {
                            const s = new Set(prev[c]);
                            if (s.has(v)) s.delete(v); else s.add(v);
                            return { ...prev, [c]: s };
                        });
                    }}
                    onCloseFilterDropdown={() => setActiveFilterDropdown(null)}
                    uniqueOptions={uniqueOptions}
                    layoutSettings={layoutSettings}
                    openHistoryModal={openHistoryModal}
                    handleCellChange={handleCellChange}
                    openControlModal={openControlModal}
                    pendingChanges={pendingChanges}
                    openCommentModal={openCommentModal}
                    handleCancelChange={handleCancelChange}
                    openCellPopout={openCellPopout}
                    getMetricColor={getMetricColor}
                    calculateActionPriority={calculateActionPriority}
                    getRiskLevel={getRiskLevel}
                    fmeaStandard={allData.projectMetadata?.fmeaStandard}
                />
            </div>

            {/* Layout Settings Panel (portal-style, rendered over the table) */}
            {layoutSettingsOpen && (
                <LayoutSettingsPanel
                    settings={layoutSettings}
                    onSettingsChange={setLayoutSettings}
                    onClose={() => setLayoutSettingsOpen(false)}
                    availableColumns={standardConfig.columns}
                />
            )}

            {isCommentModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-card rounded-2xl shadow-2xl p-8 w-full max-w-md border border-border animate-in fade-in zoom-in duration-300">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600">
                                <CheckCircle size={24} />
                            </div>
                            <h2 className="text-2xl font-bold text-text-primary">Verify Change</h2>
                        </div>
                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2 uppercase tracking-tighter">Sign-off Initials</label>
                                <input value={user} onChange={(e) => setUser(e.target.value)} className="w-full bg-background p-3 rounded-xl border border-border focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none" placeholder="e.g. JB" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2 uppercase tracking-tighter">Modification Note</label>
                                <textarea value={comment} onChange={(e) => setComment(e.target.value)} className="w-full bg-background p-3 rounded-xl border border-border h-28 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none resize-none" placeholder="Brief rationale..." />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={() => setCommentModalOpen(false)} className="px-5 py-2.5 rounded-xl font-bold text-text-secondary hover:bg-slate-100 transition-colors">Abort</button>
                            <button onClick={handleCommitChange} className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-all">Authenticate & Log</button>
                        </div>
                    </div>
                </div>
            )}

            {isHistoryModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-6">
                    <div className="bg-card rounded-2xl shadow-2xl p-8 w-full max-w-5xl max-h-[85vh] flex flex-col border border-border animate-in fade-in zoom-in duration-300">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-text-primary">Change History for {selectedFmeaId}</h2>
                            <button onClick={() => setHistoryModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={24} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto rounded-xl border border-border scrollbar-thin">
                            <table className="w-full text-sm text-left border-collapse">
                                <thead className="bg-slate-50 dark:bg-slate-900 sticky top-0 border-b border-border">
                                    <tr>
                                        <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-wider text-text-secondary">Timestamp</th>
                                        <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-wider text-text-secondary">User</th>
                                        <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-wider text-text-secondary">Field</th>
                                        <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-wider text-text-secondary">Old Val</th>
                                        <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-wider text-text-secondary">New Val</th>
                                        <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-wider text-text-secondary">Comment</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border/50">
                                    {selectedHistory?.map((rec, i) => (
                                        <tr key={i} className="hover:bg-slate-50/50">
                                            <td className="px-4 py-3 text-xs whitespace-nowrap">{new Date(rec.timestamp).toLocaleString()}</td>
                                            <td className="px-4 py-3 font-bold text-primary">{rec.user}</td>
                                            <td className="px-4 py-3 text-xs font-mono">{rec.field}</td>
                                            <td className="px-4 py-3 text-xs max-w-[120px] truncate opacity-60" title={String(rec.oldValue)}>{rec.oldValue}</td>
                                            <td className="px-4 py-3 text-xs max-w-[120px] truncate text-emerald-600 font-medium" title={String(rec.newValue)}>{rec.newValue}</td>
                                            <td className="px-4 py-3 text-xs leading-relaxed italic text-text-secondary">{rec.comment}</td>
                                        </tr>
                                    ))}
                                    {(!selectedHistory || selectedHistory.length === 0) && (
                                        <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">No historical records found for this resource.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {cellPopout && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-card rounded-2xl shadow-2xl p-8 w-full max-w-3xl max-h-[90vh] overflow-auto border border-border animate-in fade-in zoom-in duration-300">
                        <div className="flex items-center justify-between mb-8 border-b border-border pb-4">
                            <h2 className="text-2xl font-bold flex items-center gap-3">
                                <Maximize2 size={24} className="text-primary" />
                                {cellPopout.label} Exploration
                            </h2>
                            <button onClick={() => setCellPopout(null)} className="p-2 hover:bg-slate-100 rounded-full"><X size={24} /></button>
                        </div>
                        <div className="space-y-6">
                            <div className="p-5 rounded-2xl border border-border bg-slate-50 dark:bg-slate-900/50">
                                <p className="text-[10px] font-black text-text-secondary uppercase tracking-widest mb-3">Live Persistence Value</p>
                                <p className="whitespace-pre-wrap text-text-primary text-sm leading-relaxed">{cellPopout.value || <span className="italic opacity-30">Null</span>}</p>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-text-secondary uppercase tracking-widest mb-3">Draft Workspace</label>
                                <textarea value={popoutEditValue} onChange={(e) => setPopoutEditValue(e.target.value)} className="w-full p-5 rounded-2xl border border-border bg-background text-text-primary text-sm min-h-[200px] focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-y" placeholder="Refine content..." />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button onClick={() => setCellPopout(null)} className="px-6 py-2.5 rounded-xl font-bold text-text-secondary hover:bg-slate-100 transition-colors">Dismiss</button>
                                <button onClick={handlePopoutSave} className="bg-emerald-600 text-white px-8 py-2.5 rounded-xl font-bold shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition-all">Verify & Commit</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {controlModal.isOpen && (
                <ControlSelectionModal
                    isOpen={controlModal.isOpen}
                    onClose={() => setControlModal(prev => ({ ...prev, isOpen: false }))}
                    onSelect={handleControlSelect}
                    type={controlModal.type}
                    existingValue={controlModal.value}
                    availableControls={filteredControls}
                />
            )}
        </div>
    );
};

export default FmeaEditorPage;
